import { Fragment } from "react";
import { Menu as M, Transition } from "@headlessui/react";
import { useAuthContext } from "providers/Auth";
import { getAvatarInitials } from "shared/utils/avatar-initials";
import { ChevronSelectorVertical } from "untitledui-js-base";
import { NavLink } from "./NavLink";
import { logout } from "api/landlord/auth";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { cx } from "shared/utils";
import {
  HelpCircleIcon,
  LogoutSquare01Icon,
  NotificationSquareIcon,
  Settings01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

export const Menu = () => {
  const { partner, auth } = useAuthContext();
  const { show } = useIntercom();
  const navigate = useNavigate();

  const { mutate: mutateLogout } = useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      // Navigate directly to /login to clear location state
      // This prevents navigating back to previous page on next login
      window.location.href = "/login";
    },
    onError: (err: Error) => toast.error(err.message)
  });

  return (
    <M as="div" className="relative">
      <M.Button className="flex w-full items-center gap-2 border-t border-alpha-40 px-5 py-3 focus:-outline-offset-4">
        <span className="sr-only">Open user menu</span>
        <div className="flex h-10 shrink-0  w-10 items-center justify-center rounded-full border border-alpha-40 bg-primary-20 text-xs text-primary-500">
          {getAvatarInitials(partner?.name || auth?.name || "")}
        </div>
        <p
          className={cx(
            "max-w-[124px] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-content-500"
          )}
        >
          {partner?.name || auth?.name || ""}
        </p>
        <ChevronSelectorVertical
          className={cx("ml-auto h-5 w-5 stroke-content-200")}
        />
      </M.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <M.Items className="absolute -top-[200px] left-[80px] z-10 w-60  rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <M.Item>
            <NavLink
              target="_blank"
              rel="noopener"
              to="https://www.boompay.app/product-updates"
              HugeIcon={NotificationSquareIcon}
              className="px-4 !gap-3 border-b border-alpha-40 text-content-1000 "
            >
              <p className="text-base font-medium text-content-1000">
                Product updates
              </p>
            </NavLink>
          </M.Item>
          <M.Item>
            <NavLink
              HugeIcon={HelpCircleIcon}
              className="px-4 !gap-3 border-b border-alpha-40"
              onClick={() => show()}
            >
              <p className="text-base font-medium text-content-1000">Support</p>
            </NavLink>
          </M.Item>
          <M.Item>
            <NavLink
              HugeIcon={Settings01Icon}
              className="px-4 !gap-3 border-b border-alpha-40"
              onClick={() => navigate("/settings")}
            >
              <p className="text-base font-medium text-content-1000">
                Settings
              </p>
            </NavLink>
          </M.Item>
          <M.Item>
            <NavLink
              HugeIcon={LogoutSquare01Icon}
              className="px-4 !gap-3 border-b border-alpha-40"
              onClick={() => {
                window.sessionStorage.removeItem("token");
                window.sessionStorage.clear();
                mutateLogout();
              }}
            >
              <p className="text-base font-medium text-content-1000">Logout</p>
            </NavLink>
          </M.Item>
        </M.Items>
      </Transition>
    </M>
  );
};
